import { useEffect, useRef, useState } from 'react';
import { banner as bannerApi, type BannerInfo } from '../../api';

export default function BannerPage() {
  const [info, setInfo] = useState<BannerInfo | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Track the current object URL so we can revoke it on refresh/unmount.
  const objectUrlRef = useRef<string | null>(null);

  function setPreview(url: string | null) {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (url) objectUrlRef.current = url;
    setPreviewUrl(url);
  }

  async function refresh() {
    try {
      const i = await bannerApi.get();
      setInfo(i);
      setErr(null);
      if (i.banner || i.usingPlatformDefault) {
        // Auth-safe: fetch through the api() client (sends the bearer token),
        // get an object URL for the <img>.
        const url = await bannerApi.getImageObjectUrl();
        setPreview(url);
      } else {
        setPreview(null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    refresh();
    // Revoke any outstanding object URL on unmount.
    return () => setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onUpload() {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      alert('Choose an image file first (JPEG or PNG).');
      return;
    }
    if (!['image/jpeg', 'image/png'].includes(f.type)) {
      alert('Banner must be a JPEG or PNG image.');
      return;
    }
    setBusy(true);
    try {
      await bannerApi.upload(f);
      if (fileRef.current) fileRef.current.value = '';
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onClear() {
    if (!confirm("Clear this organization's banner? Devices will fall back to the platform default (or the built-in default).")) {
      return;
    }
    setBusy(true);
    try {
      await bannerApi.clear();
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1em' }}>
        <h2 style={{ margin: 0 }}>Idle Banner</h2>
      </div>

      {err && <div className="error" style={{ marginBottom: '1em' }}>{err}</div>}

      <div className="card" style={{ background: 'rgba(59,130,246,0.08)', borderColor: '#3b82f6',
        fontSize: '0.85em', color: 'var(--fg-3)', marginBottom: '1em' }}>
        This image is shown full-screen on a device whenever it has nothing to
        play — no schedule, no playlist, or content that hasn't finished
        downloading. It's cached on each device, so it still appears during a
        network outage. Recommended: a 1920×1080 JPEG or PNG. If you don't set
        one, devices use the platform default, then a built-in fallback — so a
        device never shows a blank white screen.
      </div>

      <h3>Current</h3>
      <div className="card" style={{ marginBottom: '1.5em' }}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Banner preview"
            style={{ maxWidth: '100%', borderRadius: '6px', display: 'block' }}
          />
        ) : (
          <div style={{ color: 'var(--fg-3)', fontStyle: 'italic' }}>
            No banner set and no platform default — devices use the built-in fallback.
          </div>
        )}
        <div style={{ marginTop: '0.75em', fontSize: '0.85em', color: 'var(--fg-3)' }}>
          {info?.banner ? (
            <>
              Org banner set{info.banner.updatedByEmail ? ` by ${info.banner.updatedByEmail}` : ''}
              {info.banner.updatedAt ? ` on ${new Date(info.banner.updatedAt).toLocaleString()}` : ''}.
            </>
          ) : info?.usingPlatformDefault ? (
            <>No org banner — currently using the platform default.</>
          ) : (
            <>No org banner and no platform default.</>
          )}
        </div>
      </div>

      <h3>Replace</h3>
      <div className="card">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png" disabled={busy} />
        <div style={{ marginTop: '1em', display: 'flex', gap: '0.5em' }}>
          <button className="primary" onClick={onUpload} disabled={busy}>
            {busy ? 'Uploading…' : 'Upload banner'}
          </button>
          {info?.banner && (
            <button onClick={onClear} disabled={busy}>
              Clear (use default)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
